import React, { useEffect, useState, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAlert } from 'react-alert';
import { UserContext } from "../../../UserContext";

import CenteredPageContainer from "../layout_components/CenteredPageContainer";
import Button from "../common_components/Button";
import TextButton from "../common_components/TextButton";
import MissionCardLayout from "../layout_components/MissionCardLayout";
import MissionCard from "./MissionCard"
import FilterButton from "./FilterButton";
import CenteredLoadingIcon from "../common_components/CenteredLoadingIcon";
import { DateTime } from "luxon";

// API Calls
import MissionAPI from "../../api/api_calls/MissionAPI";
import InterviewsAPI from "../../api/api_calls/InterviewsAPI";


function MissionsPage(props) {
    const { user, setUser, refreshUser } = useContext(UserContext);
    const alert = useAlert();
    const navigate = useNavigate();
    const [missions, setMissions] = useState([]);
    const [pastMissions, setPastMissions] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState("all");
    const [requiresScheduling, setRequiresScheduling] = useState(null);
    const [loadMissionsComplete, setLoadMissionsComplete] = useState(false);

    const [nextInterview, setNextInterview] = useState(null);
    const [loadNextInterviewComplete, setLoadNextInterviewComplete] = useState(false);
    const [timeZone, setTimeZone] = useState(null);
    const [referredForMissionId, setReferredForMissionId] = useState(null);
    const missionsToShow = useMemo(() => {
        if (selectedFilter === "all") {
            return missions;
        } else if (selectedFilter === "new") {
            return missions.filter((mission) => mission.mission_filter_status === "new");
        } else if (selectedFilter === "inProgress") {
            return missions.filter((mission) => mission.mission_filter_status === "inProgress");
        } else if (selectedFilter === "past") {
            return pastMissions;
        }
    }, [missions, selectedFilter, pastMissions]);

    const screenWidth = window.innerWidth;
    const isMobile = screenWidth < 840;

    useEffect(() => {
        // Grab information on this user

        async function getMissions() {
            const result = await MissionAPI.index(navigate);

            if (result.success) {
                setMissions(result.available_missions);
                // Set past missions to result.completed_missions + result.rejected_missions
                setPastMissions(result.completed_missions.concat(result.rejected_missions));
                const requiresScheduling = result.available_missions.filter((mission) => mission.requires_scheduling);
                const firstRequiresScheduling = requiresScheduling.length > 0 ? requiresScheduling[0] : null;
                setRequiresScheduling(firstRequiresScheduling);
                setReferredForMissionId(result.referred_for_mission_id);
                setLoadMissionsComplete(true);
            } else {
                setLoadMissionsComplete(false);
            }
        }

        async function getNextInterview() {
            const result = await InterviewsAPI.index(navigate);

            if (result.success) {
                const nextInterview = result.scheduled_events && result.scheduled_events.length > 0 ? result.scheduled_events[0] : null;
                setNextInterview(nextInterview);
                setLoadNextInterviewComplete(true);
                setTimeZone(result.timezone);
            } else {
                setLoadNextInterviewComplete(false);
            }
        }

        getMissions();
        getNextInterview();
        document.title = 'Missions | Despark';
    }, []);



    return (
        <CenteredPageContainer noPadding={isMobile} startAtTopMobile={true}>
            <MissionCardLayout>
                {loadNextInterviewComplete && nextInterview && timeZone &&
                    <div className="next-interview-card-container-desktop">
                        <NextInterviewCard nextInterview={nextInterview} timeZone={timeZone} isMobile={isMobile} />
                    </div>
                }
                {loadMissionsComplete && requiresScheduling &&
                    <div className="next-interview-card-container-desktop">
                        <PleaseScheduleCard requiresScheduling={requiresScheduling} isMobile={isMobile} />
                    </div>
                }
                <MissionCardLayoutHeader selectedFilter={selectedFilter} setSelectedFilter={setSelectedFilter} isMobile={isMobile} />
                {loadNextInterviewComplete && nextInterview && timeZone &&
                    <div className="next-interview-card-container-mobile">
                        <NextInterviewCard nextInterview={nextInterview} timeZone={timeZone} isMobile={isMobile} />
                    </div>
                }
                {loadMissionsComplete && requiresScheduling &&
                    <div className="next-interview-card-container-mobile">
                        <PleaseScheduleCard requiresScheduling={requiresScheduling} isMobile={isMobile} />
                    </div>
                }
                <div className="mission-cards-container">
                    {loadMissionsComplete ? <MissionCards missions={missionsToShow} isMobile={isMobile} referredForMissionId={referredForMissionId} /> : <CenteredLoadingIcon />}
                </div>
            </MissionCardLayout>
        </CenteredPageContainer>
    )
}

function MissionCards({ missions, isMobile, referredForMissionId }) {
    const navigate = useNavigate();
    // If no missions are found, show a message
    if (missions.length === 0) {
        return (
            <div className="no-missions-placeholder">
                <div className="body-medium grey-400-text">
                    No missions found. Qualify for more missions by completing profile boosters!
                </div>
                <Button size="medium" color="primary" fullWidth={isMobile} onClick={() => navigate("/boosters/")}>Edit profile</Button>
            </div>
        )
        // If missions are found, show the missions
    } else {
        return (
            <>
                {missions.map((missionContainer) => (
                    <MissionCard
                        key={missionContainer.mission.id}
                        mission={missionContainer.mission}
                        format={missionContainer.format}
                        reward={missionContainer.mission_card_reward}
                        status={missionContainer.mission_user_status}
                        spotsRemaining={missionContainer.num_remaining_spots}
                        missionType={missionContainer.mission_type}
                        missionLocked={missionContainer.mission_locked}
                        lockedReason={missionContainer.locked_reason}
                        referralBonusAmount={missionContainer.referral_bonus_amount}
                        isReferredForMission={referredForMissionId === missionContainer.mission.id} />
                ))}
            </>
        )
    }
}

function MissionCardLayoutHeader({ selectedFilter, setSelectedFilter, isMobile }) {
    if (isMobile) {
        return (
            <div className="mission-card-layout-header-mobile">
                <FilterButton text="All" isMobile={true} onClick={() => setSelectedFilter("all")} selected={selectedFilter === "all"} />
                <FilterButton text="New" isMobile={true} onClick={() => setSelectedFilter("new")} selected={selectedFilter === "new"} />
                <FilterButton text="In progress" isMobile={true} onClick={() => setSelectedFilter("inProgress")} selected={selectedFilter === "inProgress"} />
                <FilterButton text="Past missions" isMobile={true} onClick={() => setSelectedFilter("past")} selected={selectedFilter === "past"} />
            </div>
        )
    } else {
        //  TO ADD - HIGHLIGHT CARDS
        return (
            <div className="mission-card-layout-header">
                <div className="mission-header-title-and-filters white-text">
                    <h2>Missions</h2>
                    <div className="mission-header-filters">
                        <FilterButton text="All" onClick={() => setSelectedFilter("all")} selected={selectedFilter === "all"} />
                        <FilterButton text="New" onClick={() => setSelectedFilter("new")} selected={selectedFilter === "new"} />
                        <FilterButton text="In progress" onClick={() => setSelectedFilter("inProgress")} selected={selectedFilter === "inProgress"} />
                    </div>
                </div>
                <FilterButton text="Past missions" onClick={() => setSelectedFilter("past")} selected={selectedFilter === "past"} />
            </div>
        )
    }
}

function NextInterviewCard({ nextInterview, timeZone, isMobile }) {
    const navigate = useNavigate();

    // Get date in format "Monday, Dec 12"
    const start = DateTime.fromISO(nextInterview.start_time, { zone: timeZone })
    const end = DateTime.fromISO(nextInterview.end_time, { zone: timeZone })

    const date = start.toFormat("cccc, LLL d");
    const startTime = start.toFormat("h:mma").toLowerCase();
    const endTime = end.toFormat("h:mma").toLowerCase();


    return (
        <div className="next-interview-card">
            <div className="next-interview-card-title">
                {isMobile ?
                    (
                        <div className="next-interview-card-title-mobile">
                            <div>Your next interview:</div>
                            <div className="next-interview-card-date">{date + " • "}</div>
                            <div className="next-interview-card-date">{startTime + " - " + endTime}</div>
                        </div>
                    )
                    :
                    (<div>Your next interview: <span className="next-interview-card-date">{date + " • " + startTime + " - " + endTime}</span></div>)
                }
            </div>
            <Button size="small" color="primary" onClick={() => navigate(`/interviews`)}>My interviews</Button>
        </div>
    )
}

function PleaseScheduleCard({ requiresScheduling }) {
    const navigate = useNavigate();

    // Get date in format "Monday, Dec 12"
    const name = requiresScheduling.mission.name

    return (
        <div className="next-interview-card schedule-card">
            <div className="next-interview-card-title">
                <div>
                    Please schedule your interview for <span className="next-interview-card-date">{name}</span>
                </div>

            </div>
            <Button size="small" color="primary" onClick={() => navigate(`/missions/${requiresScheduling.mission.id}`)}>Schedule</Button>
        </div>
    )
}




export default MissionsPage